import React, { useEffect, useState, useRef } from "react";
import { Avatar, Button, Col, Input, Modal, Rate, Row, Drawer, message } from "antd";
import { PiBuildingOffice, PiPaperclipThin } from "react-icons/pi";
import { GoDotFill } from "react-icons/go";
import { BsFillSendFill } from 'react-icons/bs';
import { RiGalleryLine } from 'react-icons/ri';
import { FaArrowRightArrowLeft } from "react-icons/fa6";
import { IoMdCheckmarkCircleOutline } from "react-icons/io";
import {
  FaBuilding,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaClock
} from "react-icons/fa";
import { usePresence } from "../../../context/PresenceContext";
import ajaxService from "../../../services/ajax-service";
import chatService from '../../../services/chat-service';
// import echo from "../../../echo"; 

export default function ChatSystem() {
  /* =========== AUTH USER ========== */
  const user = JSON.parse(localStorage.getItem("user"));

  /* ========== STATE =============== */
  const [roles, setRoles] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  const [selectedRoleId, setSelectedRoleId] = useState(user?.role_id);
  const [activeQueue, setActiveQueue] = useState("new");
  // chats
  const [chats, setChats] = useState([]);
  const [loadingChats, setLoadingChats] = useState(false);
  // active chat
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState("");
  const [inputValue, setInputValue] = useState("");
  // modal
  const [visible, setVisible] = useState(false);
  // events
  const [ringingChat, setRingingChat] = useState(null);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferRoleId, setTransferRoleId] = useState(null);
  const [transferAgentId, setTransferAgentId] = useState(null);
  const [file, setFile] = useState(null);
  // const [onlineUsers, setOnlineUsers] = useState([]);
  const fileInputRef = useRef(null);


  const [rating, setRating] = useState(5);
  const [note, setNote] = useState("");

  /*  Message Sound  */
  const messageAudioRef = useRef(null);
  useEffect(() => {
    messageAudioRef.current = new Audio("/sounds/message.mp3");
  }, []);

  const playMessageSound = () => {
    messageAudioRef.current?.play().catch(() => { });
  };

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

  /* ==========  FETCH CHATS  =========== */
  useEffect(() => {
    if (!selectedRoleId) return;

    setLoadingChats(true);

    ajaxService
      .get(`/chat-requests?role_id=${selectedRoleId}`)
      .then(res => {
        setChats(res?.data ?? []);
      })
      .catch(() => {
        setChats([]);
      })
      .finally(() => {
        setLoadingChats(false);
      });
  }, [selectedRoleId]);

  const safeChats = Array.isArray(chats) ? chats : [];

  const queueCounts = {
    all: safeChats.length,
    new: safeChats.filter(c => c.status === "new").length,
    active: safeChats.filter(c => c.status === "active").length,
    waiting: safeChats.filter(c => c.status === "waiting").length,
    closed: safeChats.filter(c => c.status === "closed").length,
  };

  const filteredChats = safeChats.filter(chat => {
    if (activeQueue === "all") return true;
    return chat.status === activeQueue;
  });

  const isMuted = (chat) => {
    if (!chat.muted_until) return false;
    return new Date(chat.muted_until) > new Date();
  };

  const canOpenChat = (chat, user) => {
    if (!user) return false;

    // agent owns active chat
    if (chat.status === "active") {
      return true;
    }

    // closed chats can be opened read-only
    if (chat.status === "closed") {
      return true;
    }

    return false;
  };


  const canAcceptChat = (chat, user) => {
    if (!user) return false;

    // must belong to same department
    // if (chat.role_id !== user.role_id) return false;

    // // already owned
    // if (chat.assigned_agent_id !== null) return false;

    // allow NEW and WAITING (even if muted)
    if (!["new", "waiting"].includes(chat.status)) return false;

    return true;
  };

  /* ============ FETCH DEPARTMENTS (ROLES) ============ */
  useEffect(() => {
    ajaxService
      .get("/roles")
      .then(res => {
        const rolesData = res?.data ?? [];
        setRoles(rolesData);

        // select logged-in user's department by default
        if (user?.role_id) {
          setSelectedRoleId(user.role_id);
        }
      })
      .catch(() => {
        setRoles([]);
      });
  }, [user?.role_id]);

  /* ================= FETCH TEAM MEMBERS ============== */
  useEffect(() => {
    ajaxService
      .get("/users")
      .then(res => {
        setTeamMembers(res?.data ?? []);
      })
      .catch(() => {
        setTeamMembers([]);
      });
  }, []);

  /* ========== FETCH SELECTED CHAT MESSAGES =========== */
  const activeChatId = activeChat?.id ?? null;
  useEffect(() => {
    if (!activeChatId) {
      setMessages([]);
      return;
    }

    setLoadingMessages(true);

    ajaxService
      .get(`/chat-requests/${activeChatId}/messages`)
      .then(res => {
        const apiMessages = Array.isArray(res?.data)
          ? res.data
          : [];

        const mapped = apiMessages.map(msg => {
          // Determine message type
          let type;
          if (msg.sender_type === "customer") type = "received";
          else if (msg.sender_type === "agent") type = "sent";
          else if (msg.sender_type === "system") type = "system";

          return {
            type,
            text:
              typeof msg.message === "string"
                ? msg.message
                : msg.message?.toString?.() ?? "",
            time: msg.created_at
              ? new Date(msg.created_at).toLocaleTimeString()
              : "",
            username:
              msg.sender_type === "customer"
                ? activeChat?.customer?.name ?? "Customer"
                : user?.name ?? "Agent",
            avatar: activeChat?.customer?.profile_image ?? activeChat.customer?.name?.[0],
          }
        });

        setMessages(mapped);
        return chatService.markRead(activeChatId);
        // return ajaxService.post(
        //   `/chat-requests/${activeChatId}/mark-read`
        // );
      })
      .then(() => {
        setChats(prev =>
          prev.map(c =>
            c.id === activeChatId
              ? { ...c, unread_count: 0 }
              : c
          )
        );
      })
      .catch(() => {
        setMessages([]);
      })
      .finally(() => {
        setLoadingMessages(false);
      });

  }, [activeChatId]);

  useEffect(() => {
    if (!user?.role_id) return;
    loadChats();
  }, [user?.role_id]);

  /* ========== FILTER USERS BY SELECTED DEPARTMENT ========*/
  const departmentUsers = teamMembers.filter(
    member => member.role_id === selectedRoleId
  );

  /* ======== USER STATUS  === */
  const onlineUsers = usePresence();
  const onlineAgents = onlineUsers.filter(
    u => u.role_id !== null
  );
  const getUserStatus = (userId) => {

    const isOnline = onlineAgents.some(
      u => u.id === userId
    );

    if (!isOnline) return "offline";

    const hasActiveChat = chats.some(
      c =>
        c.assigned_agent_id === userId &&
        c.status === "active"
    );

    return hasActiveChat ? "busy" : "online";
  };

  /* ========= HANDLERS =========== */
  const handleSend = async () => {
    if (!inputValue.trim() || !activeChatId) return;
    // const formData = new FormData();
    // if (inputValue) formData.append("message", inputValue);
    // if (file) formData.append("file", file);

    try {
      const payload = {
        // chat_request_id: activeChatId,
        message: inputValue,
        role_id: user?.role_id,
        file: file,
        user_id: user.id
      };
      const res = await chatService.sendMessage(activeChatId, payload);
      // const res =  await chatService.sendMessage(activeChatId, {
      //     message: inputValue,
      //     file,
      //   });
      const msg = res?.data;

      setMessages(prev => [
        ...prev,
        {
          type: "sent",
          text:
            typeof msg?.message === "string"
              ? msg.message
              : inputValue,
          time: msg?.created_at
            ? new Date(msg.created_at).toLocaleTimeString()
            : new Date().toLocaleTimeString(),
          username: user?.name ?? "Agent",
          avatar: null,
        }
      ]);

      setInputValue("");
      setFile(null);
      return chatService.markRead(activeChatId);

    } catch (e) {
      console.error("Send failed", e);
    }
  };

  const handleCancel = () => {
    setVisible(false);
  }

  const handleChatClick = (chat) => {
    if (!canOpenChat(chat, user)) {
      return; // do nothing
    }

    setActiveChat(chat);
  };


  const handleAcceptChat = async (chatId) => {
    try {
      // await ajaxService.post(`/chat-requests/${chatId}/accept`);
      await chatService.accept(chatId);
      // Optimistic UI update
      setChats(prev =>
        prev.map(c =>
          c.id === chatId
            ? {
              ...c,
              status: "active",
              assigned_agent_id: user.id,
            }
            : c
        )
      );

      // Open chat immediately
      const acceptedChat = chats.find(c => c.id === chatId);
      if (acceptedChat) {
        setActiveChat({
          ...acceptedChat,
          status: "active",
          assigned_agent_id: user.id,
        });
      }

    } catch (err) {
      message.error("Failed to accept chat");
    }
  };

  const loadChats = async () => {
    try {
      const res = await ajaxService.get(
        `/chat-requests?role_id=${user.role_id}`
      );

      const data = res?.data ?? [];
      setChats(data);
    } catch (err) {
      console.error("Failed to load chats", err);
    }
  };

  const openTransferDrawer = () => {
    setTransferRoleId(activeChat?.role_id ?? null);
    setTransferAgentId(null);
    setShowTransfer(true);
  };

  const confirmTransfer = async () => {

    const payload = {
      role_id: transferRoleId,
      assigned_agent_id: transferAgentId ?? null,
      message: "Chat transfer to another department/agent"
    };
    const res = await chatService.transfer(activeChatId, payload);


    //  const res =   await ajaxService.post(`/chat-requests/${activeChatId}/transfer`,
    //     {
    //       role_id: transferRoleId,
    //       assigned_agent_id: transferAgentId ?? null,
    //       message: "Chat transfer to another department/agent"
    //     }
    //   );

    if (res?.success) {
      // remove immediately
      setChats(prev =>
        prev.filter(c => c.id !== activeChatId)
      );

      setActiveChat(null);
      setShowTransfer(false);
    }

  };

  const handleResolve = async () => {
    if (!activeChatId) return;
    // await ajaxService.post(
    //   `/chat-requests/${activeChatId}/close`,
    //   { rating, note, chat_request_id: activeChatId }
    // );
    const payload = { rating, note, chat_request_id: activeChatId };
    const res = await chatService.resolve(activeChatId, payload);
    setVisible(false);
    setActiveChat(null);
    setRating(5);
    setNote("");
  };

  // Department wise channel
  useEffect(() => {
    if (!user?.role_id || !window.Echo) return;

    const channel = window.Echo.private(
      `department.${user.role_id}`
    );

    channel
      .listen(".chat-accepted", (e) => {
        loadChats();
        // if this agent accepted it, open chat
        if (e.assigned_agent_id === user.id) {
          setActiveChat(e.chat_id);
        }
      })
      .listen(".chat-transferred", (e) => {
        const transferredChatId = e.chat_id;

        setChats(prev =>
          prev.filter(c => c.id !== transferredChatId)
        );

        if (activeChatId === transferredChatId) {
          setActiveChat(null);
        }

        loadChats();
      })
      .listen(".chat-closed", (e) => {
        setChats(prev =>
          prev.filter(c => c.id !== e.chat_id)
        );
        setActiveChat(null);
        loadChats();
      });

    return () => {
      window.Echo.leave(`department.${user.role_id}`);
    };
  }, [user?.role_id, activeChatId]);

  useEffect(() => {
    if (!activeChatId || !window.Echo) return;

    const chatChannel = window.Echo.private(`chat.${activeChatId}`);

    chatChannel.listen(".chat-message", (e) => {
      const time = new Date(e.created_at).toLocaleTimeString();
      const isAgent = e.sender_type === "agent";
      if (!isAgent) {

        let msgType;
        if (isAgent) msgType = "sent";
        else if (e.sender_type === "system") msgType = "system";
        else msgType = "received";

        setMessages(prev => [
          ...prev,
          {
            text: e.message,
            type: msgType,
            time,
            username: isAgent
              ? user?.name ?? "Agent"
              : activeChat?.customer?.name ?? "Customer",
            avatar: isAgent
              ? null
              : activeChat?.customer?.profile_image ??
              activeChat?.customer?.name?.[0],
            attachment: e.attachment ?? null,
          },
        ]);
        playMessageSound();
      }




    });

    return () => {
      window.Echo.leave(`chat.${activeChatId}`);
    };
  }, [activeChatId]);

  // useEffect(() => {
  //   if (
  //     activeChat &&
  //     !canOpenChat(activeChat, user)
  //   ) {
  //     setActiveChat(null);
  //   }
  // }, [activeChat, user]);

  const activeUsers = teamMembers.filter(user => {
    const status = getUserStatus(user.id);
    return status === "online" || status === "busy";
  });

  // set active queue 
  useEffect(() => {
    if (queueCounts.new === 0 && queueCounts.active > 0) {
      setActiveQueue("active");
    } else {
      setActiveQueue("new");
    }
  }, [queueCounts.new, queueCounts.active]);


  /* ======== RENDER ========= */
  return (
    <div className="flex gap-3 mt-6">

      {/* ================= LEFT PANEL ================= */}

      <div className="w-[27%] bg-white p-3 border rounded-2xl flex flex-col h-[calc(80vh-100px)] overflow-hidden">

        {/* SEARCH */}
        <Input
          placeholder="Search id, name, department"
          className="h-10 mb-4"
        />

        {/* QUEUE CARDS */}
        <Row gutter={[16, 16]} className='mt-2'>
          {["all", "new", "active", "waiting", "closed"].map(queue => (
            <Col span={12} key={queue} >
              <div
                onClick={() => setActiveQueue(queue)}
                className={`cursor-pointer p-2 bg-[#FAFAFA] w-full border-[#D9D9D9] border-2 rounded-xl ${activeQueue === queue ? "border-red-500" : ""
                  }`}
              >
                <p className="text-sm capitalize">{queue}</p>
                <h3 className="text-md font-semibold">
                  {queueCounts[queue]}
                </h3>
              </div>
            </Col>
          ))}
        </Row>


        {/* DEPARTMENTS */}
        {/* <div className="mb-6 mt-2">
          <h4 className="font-medium mb-2">Departments</h4>
         
            <div className="max-h-[60px] overflow-y-auto pr-1 flex gap-2 flex-wrap mt-2">
            {roles.map(role => (
            
                <p key={role.id}
                onClick={() => setSelectedRoleId(role.id)}  className={`cursor-pointer border flex items-center gap-1 px-3 py-1 rounded-lg mb-1 ${
                  selectedRoleId === role.id
                    ? "bg-red-50 text-red-600"
                    : ""
                }`}>
                <PiBuildingOffice style={{ fontSize: 14 }} /> {role.title}
                </p>
            
            ))}
          </div>
        </div> */}

        {/* TEAM MEMBERS */}
        {/* <div className="mt-4 flex flex-col flex-1 overflow-hidden">
          <h4 className="font-medium mb-2">Team Members</h4>

          {departmentUsers.length === 0 && (
            <p className="text-sm text-gray-400">
              No users in this department
            </p>
          )}

          {departmentUsers.length !== 0 && (
           <div className="flex-1 overflow-y-auto pr-1">
            {departmentUsers.map(member => {
              const status = getUserStatus(member.id);

              return (
                
                <div
                  key={member.id}
                  className="flex items-center justify-between mb-3"
                >
                  <div className="flex items-center gap-2">
                    <Avatar>{member.name?.[0]}</Avatar>
                    <div>
                      <p className="text-sm">{member.name}</p>
                      <span className="text-xs text-gray-400">
                        {member.role_name}
                      </span>
                    </div>
                  </div>

                  <span
                    className={`w-2 h-2 rounded-full ${
                      status === "online"
                        ? "bg-green-500"
                        : status === "offline"
                        ? "bg-gray-400"
                        : "bg-yellow-500"
                    }`}
                    title={status}
                  />
                </div>
              );
            })}
          </div>
          )}
        </div> */}

        <div className="mt-4 flex flex-col flex-1 overflow-hidden">
          <h4 className="font-medium mb-2">Online Team Members</h4>

          {activeUsers.length === 0 && (
            <p className="text-sm text-gray-400">
              No agents online or busy
            </p>
          )}

          {activeUsers.length > 0 && (
            <div className="flex-1 overflow-y-auto pr-1">
              {activeUsers.map(member => {
                const status = getUserStatus(member.id);

                return (
                  <div
                    key={member.id}
                    className="flex items-center justify-between mb-3"
                  >
                    <div className="flex items-center gap-2">
                      <Avatar>{member.name?.[0]}</Avatar>

                      <div>
                        <p className="text-sm">{member.name}</p>
                        <span className="text-xs text-gray-400">
                          {member.role_name}
                        </span>
                      </div>
                    </div>

                    <span
                      className={`w-2 h-2 rounded-full ${status === "online"
                          ? "bg-green-500"
                          : "bg-yellow-500"
                        }`}
                      title={status}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>

      </div>

      {/* ================= CENTER PANEL ================= h-[calc(100vh-120px)]  */}
      <div className="w-[32%] bg-white p-3 border rounded-2xl flex flex-col  h-[calc(80vh-100px)]  overflow-hidden">

        <div className='flex justify-between items-center'>
          <p>Queue</p>
          <div className={`flex gap-1 flex-wrap`}>
            {["all", "new", "active", "waiting"].map(q => (
              <p
                key={q}
                onClick={() => setActiveQueue(q)}
                className={`border flex items-center gap-1 px-2 py-1 rounded-2xl text-xs shadow-sm cursor-pointer
                          ${activeQueue === q ? "bg-primary text-white" : "bg-[#FAFAFA] text-[#444]"}`}
              >
                {q.charAt(0).toUpperCase() + q.slice(1)}
              </p>
            ))}
          </div>
        </div>


        {loadingChats && (
          <div className='flex justify-between items-center p-2 bg-[#FAFAFA] border rounded-xl'>
            <p className="text-gray-400">Loading chats...</p>
          </div>
        )}

        {!loadingChats && filteredChats.length === 0 && (
          <div className='flex justify-between items-center p-2 bg-[#FAFAFA] border rounded-xl'>
            <p className="text-gray-400">
              No chats in this queue
            </p>
          </div>
        )}

        <div className="mt-3 flex-1 overflow-y-auto pr-1">
          {!loadingChats &&
            filteredChats.map(chat => {
              const muted = isMuted(chat);
              const canAccept = canAcceptChat(chat, user);
              const showUnread = chat.unread_count > 0;

              return (
                <div
                  onClick={() => handleChatClick(chat)}
                  key={chat.id}
                  className={`p-2 mb-2 rounded-xl border cursor-pointer
                        ${activeChat?.id === chat.id
                      ? "bg-red-50 border-red-400"
                      : "bg-[#FAFAFA]"}`}
                >
                  {/* TOP ROW: NAME + TIME */}
                  <div className="flex justify-between items-start">
                    <div className="flex gap-2 items-center">
                      <Avatar size="large">
                        {chat.customer?.name?.[0] ?? "U"}
                      </Avatar>

                      <div>
                        <p className="m-0 font-semibold">
                          {chat.customer?.name}
                          <span className="text-xs text-[#71717B] ml-2">
                            C-{chat.id}
                          </span>
                        </p>
                        {/* LAST MESSAGE */}
                        <p className="mt-1 text-xs text-[#71717B]">
                          {/* {chat.last_message ?? ""} */}
                          {chat.customer?.email} <br />{chat.customer?.phone}
                        </p>
                      </div>
                    </div>

                    {/* TIME FIXED TOP RIGHT */}
                    <p className="text-xs text-[#71717B]">
                      {chat.last_message_at
                        ? formatTime(chat.last_message_at)
                        : ""}
                    </p>
                  </div>



                  {/* TAGS + UNREAD + ACCEPT (SAME ROW) */}
                  <div className="mt-2 flex flex-wrap gap-2 items-center">
                    {/* <span className="border px-3 py-1 rounded-2xl text-xs bg-white">
                          {chat.role?.title}
                        </span> */}

                    {muted && (
                      <span className="border px-3 py-1 rounded-2xl text-xs bg-gray-200 text-gray-600">
                        Muted
                      </span>
                    )}

                    {showUnread && (
                      <span className="border px-3 py-1 rounded-2xl text-xs bg-primary text-white">
                        {chat.unread_count} unread
                      </span>
                    )}

                    {/* ACCEPT BUTTON — SAME ROW */}
                    {canAccept && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAcceptChat(chat.id);
                        }}
                        className="px-3 py-1 text-xs rounded-2xl bg-primary text-white hover:opacity-90"
                      >
                        Accept
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
        </div>

      </div>

      {/* ================= RIGHT PANEL (EMPTY FOR PHASE 1) ================= */}
      <div className="w-[41%] bg-white p-2 border rounded-2xl flex flex-col h-[calc(85vh-100px)] overflow-hidden">

        {/* HEADER */}
        <div className='flex justify-between items-center border-b-2'>
          <div className='flex p-1 gap-2'>
            {/* <Avatar size="large">
                  {activeChat?.customer?.name?.[0] ?? "U"}
            </Avatar> */}
            <Avatar size="large" src={activeChat?.customer?.profile_image || undefined}>
              {!(activeChat?.customer?.profile_image) ? (activeChat?.customer?.name?.[0]) ?? "U" : null}
            </Avatar>
            <div className='flex flex-col gap-1 justify-center'>
              <p className='m-0 text-sm'>
                {activeChat?.customer?.name || "Select a chat"}
              </p>
            </div>
          </div>

          <div className='flex gap-3'>
            <Button
              icon={<FaArrowRightArrowLeft />}
              disabled={!activeChat}
              onClick={() => {
                setTransferRoleId(activeChat.role_id);
                setTransferAgentId(null);
                openTransferDrawer(true);
              }}
            >
              Transfer
            </Button>
            <Button
              icon={<IoMdCheckmarkCircleOutline />}
              disabled={!activeChat}
              onClick={() => setVisible(true)}
            >
              Resolve
            </Button>
          </div>
        </div>

        {/* MESSAGES */}
        <div className="flex-1 p-2 overflow-hidden">
          <div className="w-full h-full rounded-lg p-1 overflow-y-auto">

            {loadingMessages && (
              <p className="text-center text-gray-400 text-sm">
                Loading messages…
              </p>
            )}

            {!loadingMessages && Array.isArray(messages) &&
              messages.map((msg, idx) => {

                if (msg.type === "system") {
                  return (
                    <div
                      key={idx}
                      className="flex justify-center my-3 "
                    >
                      <div className="bg-gray-100 text-gray-600 italic text-sm text-center">
                        {msg.text}
                      </div>
                    </div>
                  );
                }
                return (
                  <div
                    key={idx}
                    className={`flex mb-3 ${msg.type === "sent"
                        ? "justify-end"
                        : "justify-start"
                      }`}
                  >
                    {msg.type === "received" ? (
                      <div className="flex items-center gap-2 max-w-[100%]">
                        <Avatar size={40} src={msg.avatar || undefined}>
                          {!msg.avatar ? msg.username?.[0] ?? "U" : null}
                        </Avatar>


                        <div className='flex flex-col gap-2'>
                          <p className="text-xs font-semibold text-[#A0AEC0] m-0">
                            {msg.username}
                          </p>

                          <div className="bg-white text-[#646E78] px-5 py-2 rounded-lg text-sm rounded-bl-none shadow">
                            {typeof msg.text === "string" ? msg.text : ""}
                          </div>
                          {msg.attachment?.type === "image" && (
                            <img
                              src={msg.attachment.url}
                              className="mt-2 rounded-lg max-w-[200px]"
                            />
                          )}

                          {msg.attachment?.type === "file" && (
                            <a
                              href={msg.attachment.url}
                              target="_blank"
                              rel="noreferrer"
                              className="block mt-2 underline"
                            >
                              📎 {msg.attachment.name}
                            </a>
                          )}

                          <p className="text-[10px] text-gray-400 m-0">
                            {msg.time}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="max-w-[70%]">
                        <div className="bg-primary text-white px-5 py-2 rounded-lg text-sm rounded-br-none shadow">
                          {typeof msg.text === "string" ? msg.text : ""}
                        </div>
                        {msg.attachment?.type === "image" && (
                          <img
                            src={msg.attachment.url}
                            className="mt-2 rounded-lg max-w-[200px]"
                          />
                        )}

                        {msg.attachment?.type === "file" && (
                          <a
                            href={msg.attachment.url}
                            target="_blank"
                            rel="noreferrer"
                            className="block mt-2 underline"
                          >
                            📎 {msg.attachment.name}
                          </a>
                        )}
                        <p className="text-[10px] text-gray-300 mt-1 text-right">
                          {msg.time}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* INPUT */}
        <div className="p-1 m-1 flex gap-3 items-center border-t-2 pt-5">


          {/* <div className='flex gap-3 items-center'>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={e => setFile(e.target.files[0])}
            />
           <Button
              type="text"
              icon={<PiPaperclipThin style={{ fontSize: 22 }} />}
              onClick={() => fileInputRef.current.click()}
            />

            <Button
              type="text"
              icon={<RiGalleryLine style={{ fontSize: 22 }} />}
              onClick={() => fileInputRef.current.click()}
            />
          </div> */}

          <Input
            value={inputValue}
            onPressEnter={handleSend}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder="Type your message here..."
            className="border focus:shadow-none px-2"
            style={{ height: "46px" }}
            disabled={!activeChat || activeChat?.status === "closed"}
          />

          <Button
            type='primary'
            onClick={handleSend}
            disabled={!activeChat || activeChat?.status === "closed"}
            className="flex items-center justify-center px-4 py-4 text-white"
          >
            <BsFillSendFill style={{ fontSize: "12px" }} />
            Send
          </Button>
        </div>
      </div>

      {/* Transfer Slide */}
      <Drawer
        placement="right"
        width={420}
        open={showTransfer}
        onClose={() => setShowTransfer(false)}
        closable={false}
      >
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <p className="text-lg font-semibold m-0">Transfer chat</p>
          <Button type="text" onClick={() => setShowTransfer(false)}>
            Close
          </Button>
        </div>

        {/* Choose department */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">Choose department</p>

          <div className="flex gap-2 flex-wrap">
            {roles.map(role => (
              <Button
                key={role.id}
                className="rounded-xl"
                type={transferRoleId === role.id ? "primary" : "default"}
                icon={<FaBuilding />}
                onClick={() => {
                  setTransferRoleId(role.id);
                  setTransferAgentId(null);
                }}
              >
                {role.title}
              </Button>
            ))}
          </div>
        </div>

        {/* Transfer to teammate */}
        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Or transfer to an online teammate
          </p>

          <div className="space-y-2">
            {teamMembers
              .filter(u => u.role_id === transferRoleId &&
                getUserStatus(u.id) !== "offline")
              .map(user => (
                <div
                  key={user.id}
                  className="flex justify-between items-center border rounded-xl px-3 py-2"
                >
                  <div className="flex gap-3 items-center">
                    <Avatar size={36}>
                      {user.name?.charAt(0)}
                    </Avatar>

                    <div>
                      <p className="m-0 font-medium text-sm">
                        {user.name}
                      </p>
                      <p className="m-0 text-xs text-gray-500">
                        {user.role_name}
                      </p>
                    </div>
                  </div>

                  <Button
                    size="small"
                    onClick={() => setTransferAgentId(user.id)}
                    type={transferAgentId === user.id ? "primary" : "default"}
                  >
                    Transfer
                  </Button>
                </div>
              ))}
          </div>
        </div>

        {/* Customer context */}
        <div className="border rounded-xl p-3 mb-4">
          <p className="text-sm font-medium mb-2">Customer context</p>

          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <FaUser />
              <span>{activeChat?.customer?.name}</span>
            </div>

            <div className="flex items-center gap-2">
              <FaEnvelope />
              <span>{activeChat?.customer?.email}</span>
            </div>

            <div className="flex items-center gap-2">
              <FaPhone />
              <span>{activeChat?.customer?.phone ?? "—"}</span>
            </div>

            <div className="flex items-center gap-2">
              <FaClock />
              <span>Joined, just now</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t pt-4">
          <Button onClick={() => setShowTransfer(false)}>
            Cancel
          </Button>

          <Button
            type="primary"
            danger
            disabled={!transferRoleId}
            onClick={confirmTransfer}
          >
            Confirm transfer
          </Button>
        </div>
      </Drawer>

      {/* Resolve Popup */}
      <Modal
        title={<p className='text-lg font-semibold m-0'>Close Chat with Feedback</p>}
        open={visible}
        onCancel={handleCancel}
        footer={null}
        centered
      >
        <div className='space-y-6'>
          {/* Rating Section */}
          <div>
            <p className='text-[#555] text-base mb-2'>How was this chat?</p>
            <Rate value={rating} onChange={(value) => setRating(value)} style={{ fontSize: 26, color: '#FAAD14' }} />
          </div>

          {/* Note Section */}
          <div>
            <p className='text-[#555] text-base mb-2'>Optional Note</p>
            <Input.TextArea
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder='Share your thoughts...'
              className='rounded-lg'
            />
          </div>

          {/* Footer Buttons */}
          <div className='flex justify-end gap-2 pt-4 border-t border-[#f0f0f0]'>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button type='primary' onClick={handleResolve}>
              Submit & Close
            </Button>
          </div>
        </div>
      </Modal>

    </div>
  );
}
